import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  TextField,
  Button,
  Alert,
  Autocomplete
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

// Список популярных стран
const COUNTRIES = [
  { code: 'RU', name: 'Россия' },
  { code: 'US', name: 'США' },
  { code: 'GB', name: 'Великобритания' },
  { code: 'DE', name: 'Германия' },
  { code: 'FR', name: 'Франция' },
  { code: 'IT', name: 'Италия' },
  { code: 'ES', name: 'Испания' },
  { code: 'CA', name: 'Канада' },
  { code: 'AU', name: 'Австралия' },
  { code: 'JP', name: 'Япония' },
  { code: 'CN', name: 'Китай' },
  { code: 'IN', name: 'Индия' },
  { code: 'BR', name: 'Бразилия' },
  { code: 'MX', name: 'Мексика' },
  { code: 'AR', name: 'Аргентина' },
  { code: 'UA', name: 'Украина' },
  { code: 'BY', name: 'Беларусь' },
  { code: 'KZ', name: 'Казахстан' },
  { code: 'UZ', name: 'Узбекистан' },
  { code: 'KG', name: 'Киргизия' },
  { code: 'TJ', name: 'Таджикистан' },
  { code: 'AM', name: 'Армения' },
  { code: 'AZ', name: 'Азербайджан' },
  { code: 'GE', name: 'Грузия' },
  { code: 'MD', name: 'Молдова' },
  { code: 'LV', name: 'Латвия' },
  { code: 'LT', name: 'Литва' },
  { code: 'EE', name: 'Эстония' }
];

const GeoRestrictionsTab = ({ channel, onChannelChange }) => {
  const [newCountryCode, setNewCountryCode] = useState('');

  const geoRestrictions = channel.geo_restrictions || { type: 'world', countries: [] };

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    onChannelChange({
      ...channel,
      geo_restrictions: {
        type: newType,
        countries: newType === 'world' ? [] : geoRestrictions.countries
      }
    });
  };

  const handleAddCountry = (event, value) => {
    if (value && !geoRestrictions.countries.includes(value.code)) {
      onChannelChange({
        ...channel,
        geo_restrictions: {
          ...geoRestrictions,
          countries: [...geoRestrictions.countries, value.code]
        }
      });
    }
  };

  const handleRemoveCountry = (countryCode) => {
    onChannelChange({
      ...channel,
      geo_restrictions: {
        ...geoRestrictions,
        countries: geoRestrictions.countries.filter(code => code !== countryCode)
      }
    });
  };

  const getCountryName = (code) => {
    const country = COUNTRIES.find(c => c.code === code);
    return country ? country.name : code;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Гео-ограничения канала
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Настройте доступность канала по географическим регионам
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Тип ограничения</FormLabel>
        <RadioGroup
          value={geoRestrictions.type}
          onChange={handleTypeChange}
          sx={{ mt: 1 }}
        >
          <FormControlLabel
            value="world"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">Доступен во всем мире</Typography>
                <Typography variant="body2" color="text.secondary">
                  Канал будет доступен пользователям из любой страны
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="countries"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">Ограничить по странам</Typography>
                <Typography variant="body2" color="text.secondary">
                  Канал будет доступен только в выбранных странах
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {geoRestrictions.type === 'countries' && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Разрешенные страны
          </Typography>

          <Autocomplete
            options={COUNTRIES.filter(country => 
              !geoRestrictions.countries.includes(country.code)
            )}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            onChange={handleAddCountry}
            value={null}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Добавить страну"
                placeholder="Начните вводить название страны..."
                sx={{ mb: 2 }}
              />
            )}
          />

          {geoRestrictions.countries.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Выбранные страны:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {geoRestrictions.countries.map((countryCode) => (
                  <Chip
                    key={countryCode}
                    label={`${getCountryName(countryCode)} (${countryCode})`}
                    onDelete={() => handleRemoveCountry(countryCode)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Не выбрано ни одной страны. Канал будет недоступен для всех пользователей.
            </Alert>
          )}

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Как это работает:</strong> Система определяет страну пользователя по его IP-адресу 
              и показывает канал только если его страна есть в списке разрешенных.
            </Typography>
          </Alert>
        </Box>
      )}

      {geoRestrictions.type === 'world' && (
        <Alert severity="success">
          <Typography variant="body2">
            Канал будет доступен пользователям из любой страны мира.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default GeoRestrictionsTab;
